import { useRef, useState } from "react";
import { UploadCloud, Loader2, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export const ImageUpload = ({ value, onChange, testid = "image-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (value) api.post("/admin/files/delete", { url: value }).catch(() => {});
      onChange(data.url);
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      <div
        data-testid={testid}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className={`relative cursor-pointer border-2 border-dashed rounded-sm flex items-center gap-4 p-4 transition-colors ${
          drag ? "border-gold bg-gold/5" : "border-stone hover:border-ink"
        }`}
      >
        {value ? (
          <img src={value} alt="aperçu" className="h-20 w-16 object-cover bg-sand rounded-sm shrink-0" />
        ) : (
          <div className="h-20 w-16 bg-sand rounded-sm flex items-center justify-center shrink-0">
            <UploadCloud className="h-6 w-6 text-ash" strokeWidth={1.4} />
          </div>
        )}
        <div className="flex-1">
          <p className="text-sm font-medium text-ink">
            {uploading ? "Téléversement..." : value ? "Changer l'image" : "Glissez une image ici"}
          </p>
          <p className="text-xs text-ash mt-0.5">JPG, PNG, WEBP — max 8 Mo</p>
        </div>
        {uploading && <Loader2 className="h-5 w-5 text-gold animate-spin" />}
        {value && !uploading && (
          <button
            type="button"
            data-testid={`${testid}-clear`}
            onClick={(e) => {
              e.stopPropagation();
              if (value) api.post("/admin/files/delete", { url: value }).catch(() => {});
              onChange("");
            }}
            className="text-ash hover:text-destructive"
            aria-label="Retirer"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          data-testid={`${testid}-input`}
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};
